import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/ui/page-header";
import { useScope } from "@/hooks/use-scope";

import {
  ENTITIES,
  FIELD_TYPES,
  type CustomFieldEntity,
  type CustomFieldType,
  listCustomFields,
  upsertCustomField,
  deleteCustomField,
} from "@/lib/custom-fields.functions";

export const Route = createFileRoute("/_authenticated/app/admin/custom-fields")({
  component: CustomFieldsAdmin,
});

type Row = {
  id: string;
  field_key: string;
  label: string;
  description: string | null;
  field_type: CustomFieldType;
  is_required: boolean;
  is_enabled: boolean;
  default_value: unknown;
  options: Array<{ value: string; label: string }> | null;
  validation: Record<string, unknown> | null;
  sort_order: number;
};

function CustomFieldsAdmin() {
  const { tenantId } = useScope();
  const [entity, setEntity] = useState<CustomFieldEntity>("orders");

  if (!tenantId) {
    return (
      <div className="p-6">
        <PageHeader title="Custom Fields" description="Tenant-defined extra fields enforced on writes." />
        <Card className="p-6">Select a tenant in the scope picker.</Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title="Custom Fields"
        description="Declare additional fields for orders and related records. The database validates every write against active definitions."
      />
      <Tabs value={entity} onValueChange={(v) => setEntity(v as CustomFieldEntity)}>
        <TabsList>
          {ENTITIES.map((e) => (
            <TabsTrigger key={e} value={e}>{e}</TabsTrigger>
          ))}
        </TabsList>
        {ENTITIES.map((e) => (
          <TabsContent key={e} value={e}>
            <EntityEditor tenantId={tenantId} entity={e} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function EntityEditor({ tenantId, entity }: { tenantId: string; entity: CustomFieldEntity }) {
  const qc = useQueryClient();
  const listFn = useServerFn(listCustomFields);
  const upsertFn = useServerFn(upsertCustomField);
  const deleteFn = useServerFn(deleteCustomField);

  const q = useQuery({
    queryKey: ["custom-fields", tenantId, entity],
    queryFn: () => listFn({ data: { tenantId, entity, enabledOnly: false } }),
  });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Row | null>(null);

  const supportsOnlyOnOrders = entity !== "orders";

  const saveMut = useMutation({
    mutationFn: (vars: Omit<Row, "id"> & { id: string | null }) =>
      upsertFn({
        data: {
          id: vars.id ?? null,
          tenantId,
          entity,
          fieldKey: vars.field_key,
          label: vars.label,
              description: vars.description ?? null,
          fieldType: vars.field_type,
          isRequired: vars.is_required,
          isEnabled: vars.is_enabled,
          defaultValue: vars.default_value ?? null,
          options: vars.options ?? null,
          validation: vars.validation ?? null,
          sortOrder: vars.sort_order,
        },
      }),
    onSuccess: (res) => {
      if ("reason" in res && res.reason === "duplicate_key") {
        toast.error("A field with that key already exists.");
        return;
      }
      toast.success("Field saved");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["custom-fields", tenantId, entity] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["custom-fields", tenantId, entity] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {supportsOnlyOnOrders ? (
            <>Definitions for <code>{entity}</code> are stored but not yet enforced — schema enforcement currently covers <code>orders</code>.</>
          ) : (
            <>Every write to <code>orders.custom_fields</code> is validated server-side against these definitions.</>
          )}
        </p>
        <Button onClick={() => { setEditing(null); setOpen(true); }} size="sm" className="gap-1">
          <Plus className="h-4 w-4" /> New field
        </Button>
      </div>

      {q.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (q.data?.rows ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground">No fields defined yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground">
              <tr>
                <th className="py-2">Order</th>
                <th className="py-2">Key</th>
                <th className="py-2">Label</th>
                <th className="py-2">Type</th>
                <th className="py-2">Required</th>
                <th className="py-2">Enabled</th>
                <th className="py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(q.data!.rows as Row[]).map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="py-2 text-muted-foreground">{r.sort_order}</td>
                  <td className="py-2 font-mono text-xs">{r.field_key}</td>
                  <td className="py-2">{r.label}</td>
                  <td className="py-2"><Badge variant="secondary" className="text-[10px]">{r.field_type}</Badge></td>
                  <td className="py-2">{r.is_required ? "Yes" : "—"}</td>
                  <td className="py-2">{r.is_enabled ? "Yes" : "No"}</td>
                  <td className="py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(r); setOpen(true); }}>Edit</Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { if (confirm(`Delete field "${r.field_key}"?`)) deleteMut.mutate(r.id); }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <FieldEditorDialog
        open={open}
        onClose={() => setOpen(false)}
        initial={editing}
        saving={saveMut.isPending}
        onSave={(r) => saveMut.mutate(r)}
      />
    </Card>
  );
}

function FieldEditorDialog({
  open,
  onClose,
  initial,
  saving,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  initial: Row | null;
  saving: boolean;
  onSave: (r: Omit<Row, "id"> & { id: string | null }) => void;
}) {
  const blank = useMemo<Row>(
    () => ({
      id: "",
      field_key: "",
      label: "",
      description: "",
      field_type: "text",
      is_required: false,
      is_enabled: true,
      default_value: null,
      options: null,
      validation: null,
      sort_order: 0,
    }),
    [],
  );
  const seed = initial ?? blank;

  const [fieldKey, setFieldKey] = useState(seed.field_key);
  const [label, setLabel] = useState(seed.label);
  const [description, setDescription] = useState(seed.description ?? "");
  const [fieldType, setFieldType] = useState<CustomFieldType>(seed.field_type);
  const [required, setRequired] = useState(seed.is_required);
  const [enabled, setEnabled] = useState(seed.is_enabled);
  const [sortOrder, setSortOrder] = useState(seed.sort_order);
  const [optionsText, setOptionsText] = useState(
    seed.options ? seed.options.map((o) => `${o.value}|${o.label}`).join("\n") : "",
  );
  const [pattern, setPattern] = useState((seed.validation?.pattern as string) ?? "");
  const [minLength, setMinLength] = useState<string>(seed.validation?.minLength != null ? String(seed.validation.minLength) : "");
  const [maxLength, setMaxLength] = useState<string>(seed.validation?.maxLength != null ? String(seed.validation.maxLength) : "");
  const [minNum, setMinNum] = useState<string>(seed.validation?.min != null ? String(seed.validation.min) : "");
  const [maxNum, setMaxNum] = useState<string>(seed.validation?.max != null ? String(seed.validation.max) : "");

  // Reset state when opening with a different row
  useMemo(() => {
    if (open) {
      setFieldKey(seed.field_key);
      setLabel(seed.label);
      setDescription(seed.description ?? "");
      setFieldType(seed.field_type);
      setRequired(seed.is_required);
      setEnabled(seed.is_enabled);
      setSortOrder(seed.sort_order);
      setOptionsText(seed.options ? seed.options.map((o) => `${o.value}|${o.label}`).join("\n") : "");
      setPattern((seed.validation?.pattern as string) ?? "");
      setMinLength(seed.validation?.minLength != null ? String(seed.validation.minLength) : "");
      setMaxLength(seed.validation?.maxLength != null ? String(seed.validation.maxLength) : "");
      setMinNum(seed.validation?.min != null ? String(seed.validation.min) : "");
      setMaxNum(seed.validation?.max != null ? String(seed.validation.max) : "");
    }
  }, [open, seed]);

  const needsOptions = fieldType === "select" || fieldType === "multiselect";
  const isText = fieldType === "text";
  const isNumber = fieldType === "number";

  function submit() {
    if (!/^[a-z][a-z0-9_]{0,39}$/.test(fieldKey)) {
      toast.error("Field key must be snake_case (lowercase, digits, underscore).");
      return;
    }
    if (!label.trim()) {
      toast.error("Label is required.");
      return;
    }

    let options: Array<{ value: string; label: string }> | null = null;
    if (needsOptions) {
      options = optionsText
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean)
        .map((l) => {
          const [v, ...rest] = l.split("|");
          return { value: v.trim(), label: (rest.join("|").trim() || v.trim()) };
        });
      if (options.length === 0) {
        toast.error("Add at least one option (value|label per line).");
        return;
      }
    }

    const validation: Record<string, unknown> = {};
    if (isText) {
      if (minLength !== "") validation.minLength = Number(minLength);
      if (maxLength !== "") validation.maxLength = Number(maxLength);
      if (pattern) validation.pattern = pattern;
    }
    if (isNumber) {
      if (minNum !== "") validation.min = Number(minNum);
      if (maxNum !== "") validation.max = Number(maxNum);
    }

    onSave({
      id: initial?.id ?? null,
      field_key: fieldKey,
      label,
      description: description || null,
      field_type: fieldType,
      is_required: required,
      is_enabled: enabled,
      default_value: null,
      options,
      validation: Object.keys(validation).length > 0 ? validation : null,
      sort_order: sortOrder,
    });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit field" : "New field"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Key (immutable identifier)</Label>
              <Input
                value={fieldKey}
                onChange={(e) => setFieldKey(e.target.value)}
                placeholder="e.g. shopify_tag"
                disabled={!!initial}
              />
            </div>
            <div>
              <Label>Label</Label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Help text</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Shown beside the field in forms" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={fieldType} onValueChange={(v) => setFieldType(v as CustomFieldType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sort order</Label>
              <Input type="number" value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value) || 0)} />
            </div>
            <div className="flex items-end gap-4">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={required} onCheckedChange={setRequired} /> Required
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={enabled} onCheckedChange={setEnabled} /> Enabled
              </label>
            </div>
          </div>

          {needsOptions && (
            <div>
              <Label>Options — one per line as <code>value|label</code></Label>
              <Textarea
                rows={5}
                value={optionsText}
                onChange={(e) => setOptionsText(e.target.value)}
                placeholder={"high|High priority\nlow|Low priority"}
                className="font-mono text-xs"
              />
            </div>
          )}

          {isText && (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Min length</Label>
                <Input type="number" value={minLength} onChange={(e) => setMinLength(e.target.value)} />
              </div>
              <div>
                <Label>Max length</Label>
                <Input type="number" value={maxLength} onChange={(e) => setMaxLength(e.target.value)} />
              </div>
              <div>
                <Label>Regex pattern</Label>
                <Input value={pattern} onChange={(e) => setPattern(e.target.value)} placeholder="^[A-Z]{3}-\d+$" />
              </div>
            </div>
          )}
          {isNumber && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Min</Label>
                <Input type="number" value={minNum} onChange={(e) => setMinNum(e.target.value)} />
              </div>
              <div>
                <Label>Max</Label>
                <Input type="number" value={maxNum} onChange={(e) => setMaxNum(e.target.value)} />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>
            {saving && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}